import { MNSproCloudApi } from '..';
import { ConsentResponse } from '../api/v2';

export enum UserConsentState {
  pending = 'Pending',
  running = 'Running',
  accepted = 'Accepted',
  acceptedWithError = 'AcceptedWithError',
  declined = 'Declined',
}

export interface UserConsent {
  id: string;
  firstName: string;
  lastName: string;
  mailAddresses: string[];
  parentalMailAddress: string | null;
  acceptedAt: string;
  error: string | null;
  providedBy: string | null;
  providedFrom: string | null;
  state: UserConsentState;
}

const consentResponseV2ToUserConsent = (input: ConsentResponse[]) =>
  input.map<UserConsent>(input => ({
    id: input.id ?? '',
    firstName: input?.firstName ?? '',
    lastName: input?.lastName ?? '',
    mailAddresses: input.mailAddress ? [input.mailAddress] : [],
    parentalMailAddress: input.parentalMailAddress ?? null,
    acceptedAt: input.acceptedAtUtc ?? '',
    error: input.error ?? null,
    providedBy: input.providedBy ?? null,
    providedFrom: input.providedFrom ?? null,
    state: input.state
      ? (input.state as unknown as UserConsentState)
      : UserConsentState.pending,
  }));

export class UserConsentService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ConsentApiFactory(config);

    return api;
  }

  static async getAll(
    authToken: string,
    organizationId: string,
  ): Promise<UserConsent[]> {
    const api = UserConsentService.getApi(authToken);
    const response = await api.getAll(organizationId);

    const userConsent: UserConsent[] = consentResponseV2ToUserConsent(
      response.data,
    );

    return userConsent;
  }

  static async resend(
    authToken: string,
    organizationId: string,
    consentId: string,
  ) {
    const api = UserConsentService.getApi(authToken);
    await api.resendConsentMail(organizationId, consentId);
  }

  static async destroy(
    authToken: string,
    organizationId: string,
    consentId: string,
  ) {
    const api = UserConsentService.getApi(authToken);
    await api._delete(organizationId, consentId);
  }

  static async update(
    authToken: string,
    organizationId: string,
    consent: UserConsent,
  ) {
    const api = UserConsentService.getApi(authToken);
    await api.updateAccountAddress(
      organizationId,
      consent.id,
      consent.mailAddresses.join(', '),
    );
  }
}
